/**
 * Right-click context menus for region list rows and stack section headers.
 *
 * Region row menu order (Windows convention - Properties at bottom):
 *   1. Merge           (Selection group - enabled when 2+ regions selected)
 *   2. Delete          (Selection group - enabled when 1+ region selected)
 *   3. [divider]
 *   4. Create stack
 *   5. Add to stack ▶ (submenu when 2+ stacks; disabled if no stacks)
 *   6. Remove from stack (enabled only if selected region is in a stack)
 *   7. Disband stack   (enabled only if selected region is in a stack)
 *   8. [divider]
 *   9. Rename (F2)     (triggers F2 rename flow)
 *  10. Edit area boundary (disabled placeholder - Coming soon)
 *  11. [divider]
 *  12. Properties...   (Yuko R7 B2: right-click only entrypoint)
 *
 * Stack header menu order (UX-2):
 *   1. Rename stack (F2)
 *   2. [divider]
 *   3. Disband stack
 *   4. [divider]
 *   5. Properties...
 */

import { t } from '../i18n';
import type { Store } from '../state';
import { createStack, disbandStack, nextUnusedStackN, addRegionsToStack, removeRegionsFromStack } from '../util/stack-ops';
import { pushUndoSnapshot } from '../util/region-undo';
import { openRegionPropertiesPopover } from './region-properties-popover';
import { mergeRegions, deleteRegions } from '../util/region-ops';
import {
  applyStackStyleChoice,
  chooseMergeSourceStyle,
  chooseStackStyle,
  cloneMethodSnapshot,
} from './region-style-choice';

export interface RegionContextMenuOptions {
  x: number;
  y: number;
  store: Store;
  /** The region row element that was right-clicked. */
  anchor: HTMLElement;
  /** UUID of the right-clicked region. */
  regionUuid: string;
  /** Trigger an F2 rename on the region row. */
  onRename: () => void;
}

export interface StackContextMenuOptions {
  /** The stack header element that was right-clicked (used for anchoring). */
  anchor: HTMLElement;
  /** UUID of the stack. */
  stackUuid: string;
  /** Display name of the stack (for Properties popover info). */
  stackName: string;
  store: Store;
  /** Trigger an inline rename on the stack header name span. */
  onRename: () => void;
}

/** Close any currently open context menu (at most one at a time). */
let closeCurrentMenu: (() => void) | null = null;

function countRegionPixels(state: ReturnType<Store['get']>, label: number): number {
  let count = 0;
  for (let i = 0; i < state.regionLabelPlane.length; i++) {
    if (state.regionLabelPlane[i] === label) count++;
  }
  return count;
}

/**
 * Open a context menu at the given cursor position for the specified region.
 */
export function openRegionContextMenu(opts: RegionContextMenuOptions): void {
  // Close any previously open menu first.
  closeCurrentMenu?.();

  const { x, y, store, regionUuid, onRename } = opts;

  const state = store.get();
  const entry = state.regionEntries.find((e) => e.uuid === regionUuid);
  if (!entry) return;

  // Ensure the right-clicked region is selected (single-select if not already).
  const selected = state.activeRegionUuids;
  const isSelected = selected.includes(regionUuid);
  const activeUuids = isSelected ? selected : [regionUuid];
  if (!isSelected) {
    store.patch({ activeRegionUuids: [regionUuid] });
  }

  const anyInStack = activeUuids.some((uuid) => {
    const e = state.regionEntries.find((r) => r.uuid === uuid);
    return e?.stackId !== null;
  });

  const hasStacks = state.regionStacks.length > 0;

  // ── Build the menu ─────────────────────────────────────────────────────────
  const menu = document.createElement('ul');
  menu.className = 'region-context-menu';
  menu.setAttribute('role', 'menu');
  menu.style.cssText = `position:fixed;left:${x}px;top:${y}px;z-index:9999;`;

  let highlightedIndex = -1;
  const items: HTMLLIElement[] = [];

  // Submenu state hoisted to outer scope so the main close() path and the
  // outside-click handler can both reach it. Only the multi-stack branch
  // actually creates a submenu element.
  let submenu: HTMLUListElement | null = null;
  function closeSubmenu(): void {
    submenu?.remove();
    submenu = null;
  }

  function makeItem(labelText: string, disabled: boolean, onClick: () => void): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'region-context-menu-item';
    li.setAttribute('role', 'menuitem');
    li.textContent = labelText;
    if (disabled) {
      li.classList.add('disabled');
      li.setAttribute('aria-disabled', 'true');
    } else {
      li.addEventListener('click', () => {
        close();
        onClick();
      });
      li.addEventListener('mouseenter', () => {
        const idx = items.indexOf(li);
        setHighlight(idx);
      });
    }
    items.push(li);
    menu.appendChild(li);
    return li;
  }

  function makeSeparator(): void {
    const sep = document.createElement('li');
    sep.className = 'region-context-menu-sep';
    sep.setAttribute('role', 'separator');
    menu.appendChild(sep);
  }

  function setHighlight(idx: number): void {
    if (highlightedIndex >= 0) {
      items[highlightedIndex]?.removeAttribute('data-highlighted');
    }
    highlightedIndex = idx;
    if (idx >= 0) {
      items[idx]?.setAttribute('data-highlighted', 'true');
      items[idx]?.focus();
    }
  }

  // ── Group 1: Selection actions ─────────────────────────────────────────────

  // Merge - enabled when 2+ regions are selected (matches sidebar button).
  const canMerge = activeUuids.length >= 2;
  makeItem(t('action.region.merge'), !canMerge, () => {
    const currentState = store.get();
    const sel = currentState.activeRegionUuids;
    if (sel.length < 2) return;
    const undoStack = pushUndoSnapshot(currentState);
    const targetUuid = sel[0]!;
    const sourceUuids = sel.slice(1);
    const sourceStyle = chooseMergeSourceStyle(currentState, targetUuid, sourceUuids);
    const imageSource = currentState.source ?? currentState.labelMap;
    const patch = mergeRegions(
      {
        regionEntries: currentState.regionEntries,
        regionLabelPlane: currentState.regionLabelPlane,
        activeRegionUuids: currentState.activeRegionUuids,
        width: imageSource?.width ?? 0,
        height: imageSource?.height ?? 0,
        labelMap: currentState.labelMap,
        regions: currentState.regions,
      },
      targetUuid,
      sourceUuids,
    );
    const regionEntries =
      sourceStyle === null
        ? patch.regionEntries
        : patch.regionEntries.map((mergedEntry) =>
            mergedEntry.uuid === targetUuid
              ? { ...mergedEntry, override: cloneMethodSnapshot(sourceStyle) }
              : mergedEntry,
          );
    store.patch({ ...patch, regionEntries, regionUndoStack: undoStack });
  });

  // Delete - enabled when 1+ regions are selected (matches sidebar button).
  const canDelete = activeUuids.length >= 1;
  makeItem(t('action.region.delete'), !canDelete, () => {
    const currentState = store.get();
    const sel = currentState.activeRegionUuids;
    if (sel.length < 1) return;
    const undoStack = pushUndoSnapshot(currentState);
    const imageSource = currentState.source ?? currentState.labelMap;
    const patch = deleteRegions(
      {
        regionEntries: currentState.regionEntries,
        regionLabelPlane: currentState.regionLabelPlane,
        activeRegionUuids: currentState.activeRegionUuids,
        width: imageSource?.width ?? 0,
        height: imageSource?.height ?? 0,
        labelMap: currentState.labelMap,
        regions: currentState.regions,
      },
      sel,
    );
    store.patch({ ...patch, regionUndoStack: undoStack });
  });

  makeSeparator();

  // ── Group 2: Stack actions ─────────────────────────────────────────────────

  // Create stack
  makeItem(t('action.stack.create'), activeUuids.length < 1, () => {
    const currentState = store.get();
    const undoStack = pushUndoSnapshot(currentState);
    const PLACEHOLDER = '__N__';
    const stackNTemplate = t('label.stackN', { n: PLACEHOLDER });
    const prefix = stackNTemplate.replace(PLACEHOLDER, '').trim();
    const n = nextUnusedStackN(currentState.regionStacks, prefix);
    const userName = t('label.stackN', { n });
    const patch = createStack(currentState, { memberUuids: activeUuids, userName });
    store.patch({ ...patch, regionUndoStack: undoStack });
  });

  // Add to stack
  const addToStackItem = makeItem(t('action.stack.addTo'), !hasStacks, () => {
    const currentState = store.get();
    if (currentState.regionStacks.length === 1) {
      const undoStack = pushUndoSnapshot(currentState);
      const stackUuid = currentState.regionStacks[0]!.uuid;
      const styleChoice = chooseStackStyle(currentState, stackUuid, activeUuids);
      const patch = addRegionsToStack(currentState, stackUuid, activeUuids);
      const regionEntries =
        patch.regionEntries === undefined
          ? undefined
          : applyStackStyleChoice(
              currentState,
              patch.regionEntries,
              activeUuids,
              styleChoice,
            );
      const styledPatch =
        regionEntries === undefined ? patch : { ...patch, regionEntries };
      store.patch({ ...styledPatch, regionUndoStack: undoStack });
    }
    // Multi-stack case is handled by the submenu below - this click handler
    // is only reached when there is exactly 1 stack (submenu not shown).
  });

  // Multi-stack: attach a submenu listing every stack by name.
  if (state.regionStacks.length >= 2) {
    addToStackItem.setAttribute('aria-haspopup', 'true');
    // Add a visual arrow hint.
    const arrow = document.createElement('span');
    arrow.textContent = ' ▶';
    arrow.setAttribute('aria-hidden', 'true');
    addToStackItem.appendChild(arrow);

    function openSubmenu(): void {
      if (submenu) return;
      submenu = document.createElement('ul');
      submenu.className = 'region-context-menu region-context-menu-sub';
      submenu.setAttribute('role', 'menu');

      // Position to the right of the parent item.
      const rect = addToStackItem.getBoundingClientRect();
      submenu.style.cssText = `position:fixed;left:${rect.right}px;top:${rect.top}px;z-index:10000;`;

      const subItems: HTMLLIElement[] = [];
      for (const stack of state.regionStacks) {
        const sub = document.createElement('li');
        sub.className = 'region-context-menu-item';
        sub.setAttribute('role', 'menuitem');
        sub.tabIndex = -1;
        sub.textContent = stack.userName;
        sub.addEventListener('click', () => {
          close();
          const currentState = store.get();
          const undoStack = pushUndoSnapshot(currentState);
          const styleChoice = chooseStackStyle(currentState, stack.uuid, activeUuids);
          const patch = addRegionsToStack(currentState, stack.uuid, activeUuids);
          const regionEntries =
            patch.regionEntries === undefined
              ? undefined
              : applyStackStyleChoice(
                  currentState,
                  patch.regionEntries,
                  activeUuids,
                  styleChoice,
                );
          const styledPatch =
            regionEntries === undefined ? patch : { ...patch, regionEntries };
          store.patch({ ...styledPatch, regionUndoStack: undoStack });
        });
        sub.addEventListener('mouseenter', () => {
          for (const other of subItems) other.removeAttribute('data-highlighted');
          sub.setAttribute('data-highlighted', 'true');
        });
        // Submenu keyboard nav: ArrowDown/Up between items, ArrowLeft/Escape
        // back to parent, Enter activates.
        sub.addEventListener('keydown', (ke) => {
          if (ke.key === 'ArrowLeft' || ke.key === 'Escape') {
            ke.preventDefault();
            ke.stopPropagation();
            closeSubmenu();
            addToStackItem.focus();
            return;
          }
          if (ke.key === 'ArrowDown') {
            ke.preventDefault();
            const idx = subItems.indexOf(sub);
            const next = idx < subItems.length - 1 ? idx + 1 : 0;
            subItems[next]?.focus();
            return;
          }
          if (ke.key === 'ArrowUp') {
            ke.preventDefault();
            const idx = subItems.indexOf(sub);
            const prev = idx > 0 ? idx - 1 : subItems.length - 1;
            subItems[prev]?.focus();
            return;
          }
        });
        subItems.push(sub);
        submenu.appendChild(sub);
      }

      document.body.appendChild(submenu);
    }

    addToStackItem.addEventListener('mouseenter', () => {
      if (state.regionStacks.length >= 2) openSubmenu();
    });
    addToStackItem.addEventListener('mouseleave', (e) => {
      // Keep submenu open if cursor moved into it.
      if (submenu && submenu.contains(e.relatedTarget as Node)) return;
      closeSubmenu();
    });
    // ArrowRight key opens the submenu, ArrowLeft / Escape closes it.
    addToStackItem.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        openSubmenu();
        (submenu?.querySelector('.region-context-menu-item') as HTMLElement | null)?.focus();
      }
    });
    // Override click to open submenu instead of firing the single-stack path.
    addToStackItem.addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      if (submenu) {
        closeSubmenu();
      } else {
        openSubmenu();
      }
    }, true);
  }

  // Remove from stack
  makeItem(t('action.stack.removeFrom'), !anyInStack, () => {
    const currentState = store.get();
    const undoStack = pushUndoSnapshot(currentState);
    const patch = removeRegionsFromStack(currentState, activeUuids);
    store.patch({ ...patch, regionUndoStack: undoStack });
  });

  // Disband stack
  makeItem(t('action.stack.disband'), !anyInStack, () => {
    const currentState = store.get();
    const stackUuidsToDisband = new Set<string>();
    for (const e of currentState.regionEntries) {
      if (e.stackId !== null && activeUuids.includes(e.uuid)) {
        stackUuidsToDisband.add(e.stackId);
      }
    }
    if (stackUuidsToDisband.size === 0) return;
    const undoStack = pushUndoSnapshot(currentState);
    let patchedState = currentState;
    const combinedPatch: Partial<typeof currentState> = {};
    for (const uuid of stackUuidsToDisband) {
      const p = disbandStack(patchedState, uuid);
      if (p.regionStacks !== undefined) combinedPatch.regionStacks = p.regionStacks;
      if (p.regionEntries !== undefined) combinedPatch.regionEntries = p.regionEntries;
      patchedState = { ...patchedState, ...p };
    }
    store.patch({ ...combinedPatch, regionUndoStack: undoStack });
  });

  makeSeparator();

  // ── Group 3: Naming / boundary ─────────────────────────────────────────────

  // Rename (F2) - label already includes shortcut hint per C-3 (via i18n key).
  makeItem(t('action.region.rename'), false, () => {
    onRename();
  });

  // Edit area boundary (placeholder - disabled, Coming soon in v0.2.5+).
  makeItem(t('action.region.editBoundary', {}), true, () => { /* Coming soon */ });

  makeSeparator();

  // ── Group 4: Properties (Windows convention: last item) ────────────────────

  // Yuko R7 B2: Properties opens from right-click contextmenu only; the
  // previous three-dot button and dblclick paths were removed.
  makeItem(t('action.region.properties'), false, () => {
    const currentState = store.get();
    const currentEntry =
      currentState.regionEntries.find((e) => e.uuid === regionUuid) ?? entry;
    // Selecting an inactive row re-renders the region list, so the captured
    // row can be detached by the time this menu item runs. Re-query by UUID
    // and fall back only if no live row is available.
    const liveAnchor =
      document.querySelector<HTMLElement>(
        `[data-region-uuid="${CSS.escape(regionUuid)}"]`,
      ) ?? opts.anchor;
    openRegionPropertiesPopover(
      liveAnchor,
      currentEntry,
      countRegionPixels(currentState, currentEntry.label),
      currentState.regionStacks,
      store,
    );
  });

  document.body.appendChild(menu);

  // Clamp position so the menu stays in viewport.
  const rect = menu.getBoundingClientRect();
  if (rect.right > window.innerWidth) {
    menu.style.left = `${x - rect.width}px`;
  }
  if (rect.bottom > window.innerHeight) {
    menu.style.top = `${y - rect.height}px`;
  }

  // ── Keyboard navigation ────────────────────────────────────────────────────
  function handleKeydown(event: KeyboardEvent): void {
    const enabledItems = items.filter((li) => !li.classList.contains('disabled'));
    const enabledIdx = enabledItems.indexOf(items[highlightedIndex] as HTMLLIElement);

    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = enabledIdx < enabledItems.length - 1 ? enabledIdx + 1 : 0;
      setHighlight(items.indexOf(enabledItems[next] as HTMLLIElement));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = enabledIdx > 0 ? enabledIdx - 1 : enabledItems.length - 1;
      setHighlight(items.indexOf(enabledItems[prev] as HTMLLIElement));
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      items[highlightedIndex]?.click();
      return;
    }
  }

  document.addEventListener('keydown', handleKeydown, true);

  // ── Outside-click close ────────────────────────────────────────────────────
  function handleOutsideClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (menu.contains(target)) return;
    if (submenu && submenu.contains(target)) return;
    close();
  }

  // Delay attaching the outside-click listener to avoid the triggering mousedown
  // from immediately closing the menu.
  setTimeout(() => {
    document.addEventListener('click', handleOutsideClick, true);
  }, 0);

  function close(): void {
    if (closeCurrentMenu !== close) return; // Already closed.
    closeCurrentMenu = null;
    document.removeEventListener('keydown', handleKeydown, true);
    document.removeEventListener('click', handleOutsideClick, true);
    closeSubmenu();
    menu.remove();
  }

  closeCurrentMenu = close;

  // Focus the first enabled item.
  const firstEnabled = items.findIndex((li) => !li.classList.contains('disabled'));
  if (firstEnabled >= 0) {
    setHighlight(firstEnabled);
  }
}

// ── Stack header context menu (UX-2) ──────────────────────────────────────────

/**
 * Open a right-click context menu for a stack section header.
 *
 * Menu: Rename stack (F2) → [divider] → Disband stack → [divider] → Properties...
 *
 * Positioned at the top-left corner of the header element; clamped to viewport.
 */
export function openStackContextMenu(opts: StackContextMenuOptions): void {
  // Close any previously open menu first.
  closeCurrentMenu?.();

  const { anchor, stackUuid, store, onRename } = opts;

  // Position the menu at the header's top-left corner.
  const headerRect = anchor.getBoundingClientRect();
  const x = headerRect.left;
  const y = headerRect.bottom;

  // ── Build the menu ─────────────────────────────────────────────────────────
  const menu = document.createElement('ul');
  menu.className = 'region-context-menu';
  menu.setAttribute('role', 'menu');
  menu.style.cssText = `position:fixed;left:${x}px;top:${y}px;z-index:9999;`;

  let highlightedIndex = -1;
  const items: HTMLLIElement[] = [];

  function makeItem(labelText: string, disabled: boolean, onClick: () => void): HTMLLIElement {
    const li = document.createElement('li');
    li.className = 'region-context-menu-item';
    li.setAttribute('role', 'menuitem');
    li.textContent = labelText;
    if (disabled) {
      li.classList.add('disabled');
      li.setAttribute('aria-disabled', 'true');
    } else {
      li.addEventListener('click', () => {
        close();
        onClick();
      });
      li.addEventListener('mouseenter', () => {
        const idx = items.indexOf(li);
        setHighlight(idx);
      });
    }
    items.push(li);
    menu.appendChild(li);
    return li;
  }

  function makeSeparator(): void {
    const sep = document.createElement('li');
    sep.className = 'region-context-menu-sep';
    sep.setAttribute('role', 'separator');
    menu.appendChild(sep);
  }

  function setHighlight(idx: number): void {
    if (highlightedIndex >= 0) {
      items[highlightedIndex]?.removeAttribute('data-highlighted');
    }
    highlightedIndex = idx;
    if (idx >= 0) {
      items[idx]?.setAttribute('data-highlighted', 'true');
      items[idx]?.focus();
    }
  }

  // ── Group 1: Rename ────────────────────────────────────────────────────────

  makeItem(t('menu.stack.rename'), false, () => {
    onRename();
  });

  makeSeparator();

  // ── Group 2: Disband ───────────────────────────────────────────────────────

  makeItem(t('menu.stack.disband'), false, () => {
    const currentState = store.get();
    const undoStack = pushUndoSnapshot(currentState);
    const patch = disbandStack(currentState, stackUuid);
    store.patch({ ...patch, regionUndoStack: undoStack });
  });

  makeSeparator();

  // ── Group 3: Properties ────────────────────────────────────────────────────

  makeItem(t('menu.stack.properties'), false, () => {
    const currentState = store.get();
    const stack = currentState.regionStacks.find((s) => s.uuid === stackUuid);
    if (!stack) return;

    // Collect member entries for the Properties display.
    const memberEntries = currentState.regionEntries.filter(
      (e) => e.stackId === stackUuid,
    );

    // Build a lightweight popover inline. Uses the same region-props-popover
    // CSS class so it inherits existing styles without touching styles.css.
    const existingPopover = document.querySelector('.region-props-popover');
    existingPopover?.remove();

    const popover = document.createElement('div');
    popover.className = 'region-props-popover';
    popover.setAttribute('role', 'dialog');
    popover.setAttribute('aria-modal', 'false');
    popover.setAttribute('aria-label', stack.userName);

    // Stack name row.
    const nameRow = document.createElement('div');
    nameRow.className = 'region-props-row';
    const nameLabel = document.createElement('span');
    nameLabel.className = 'region-props-label';
    nameLabel.textContent = t('label.props.name');
    const nameValue = document.createElement('span');
    nameValue.className = 'region-props-value';
    nameValue.textContent = stack.userName;
    nameRow.appendChild(nameLabel);
    nameRow.appendChild(nameValue);
    popover.appendChild(nameRow);

    // Member count row.
    const countRow = document.createElement('div');
    countRow.className = 'region-props-row';
    const countLabel = document.createElement('span');
    countLabel.className = 'region-props-label';
    countLabel.textContent = t('label.stackMemberCount', { n: memberEntries.length });
    const countValue = document.createElement('span');
    countValue.className = 'region-props-value';
    countValue.textContent = String(memberEntries.length);
    countRow.appendChild(countLabel);
    countRow.appendChild(countValue);
    popover.appendChild(countRow);

    // Member UUIDs list row.
    const uuidsRow = document.createElement('div');
    uuidsRow.className = 'region-props-row';
    const uuidsLabel = document.createElement('span');
    uuidsLabel.className = 'region-props-label';
    uuidsLabel.dataset['i18n'] = 'label.members';
    uuidsLabel.textContent = t('label.members');
    const uuidsList = document.createElement('ul');
    uuidsList.className = 'region-props-value';
    uuidsList.style.cssText = 'list-style:none;padding:0;margin:0;font-size:0.75em;';
    for (const e of memberEntries) {
      const li = document.createElement('li');
      li.textContent = e.userName;
      uuidsList.appendChild(li);
    }
    uuidsRow.appendChild(uuidsLabel);
    uuidsRow.appendChild(uuidsList);
    popover.appendChild(uuidsRow);

    // Position below anchor header.
    document.body.appendChild(popover);
    const rect = anchor.getBoundingClientRect();
    popover.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.bottom}px;z-index:9998;`;

    // Clamp to viewport.
    const popRect = popover.getBoundingClientRect();
    if (popRect.right > window.innerWidth) {
      popover.style.left = `${rect.left - (popRect.right - window.innerWidth)}px`;
    }
    if (popRect.bottom > window.innerHeight) {
      popover.style.top = `${rect.top - popRect.height}px`;
    }

    // Close on outside click or Escape.
    function handlePopoverOutside(ev: MouseEvent): void {
      if (popover.contains(ev.target as Node)) return;
      popover.remove();
      document.removeEventListener('click', handlePopoverOutside, true);
      document.removeEventListener('keydown', handlePopoverKey, true);
    }
    function handlePopoverKey(ev: KeyboardEvent): void {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        popover.remove();
        document.removeEventListener('click', handlePopoverOutside, true);
        document.removeEventListener('keydown', handlePopoverKey, true);
      }
    }
    setTimeout(() => {
      document.addEventListener('click', handlePopoverOutside, true);
      document.addEventListener('keydown', handlePopoverKey, true);
    }, 0);
  });

  document.body.appendChild(menu);

  // Clamp position so the menu stays in viewport.
  const rect = menu.getBoundingClientRect();
  if (rect.right > window.innerWidth) {
    menu.style.left = `${x - rect.width}px`;
  }
  if (rect.bottom > window.innerHeight) {
    menu.style.top = `${y - rect.height}px`;
  }

  // ── Keyboard navigation ────────────────────────────────────────────────────
  function handleKeydown(event: KeyboardEvent): void {
    const enabledItems = items.filter((li) => !li.classList.contains('disabled'));
    const enabledIdx = enabledItems.indexOf(items[highlightedIndex] as HTMLLIElement);

    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = enabledIdx < enabledItems.length - 1 ? enabledIdx + 1 : 0;
      setHighlight(items.indexOf(enabledItems[next] as HTMLLIElement));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = enabledIdx > 0 ? enabledIdx - 1 : enabledItems.length - 1;
      setHighlight(items.indexOf(enabledItems[prev] as HTMLLIElement));
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      items[highlightedIndex]?.click();
      return;
    }
  }

  document.addEventListener('keydown', handleKeydown, true);

  // ── Outside-click close ────────────────────────────────────────────────────
  function handleOutsideClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (menu.contains(target)) return;
    close();
  }

  // Delay to avoid the triggering mousedown immediately closing the menu.
  setTimeout(() => {
    document.addEventListener('click', handleOutsideClick, true);
  }, 0);

  function close(): void {
    if (closeCurrentMenu !== close) return; // Already closed.
    closeCurrentMenu = null;
    document.removeEventListener('keydown', handleKeydown, true);
    document.removeEventListener('click', handleOutsideClick, true);
    menu.remove();
  }

  closeCurrentMenu = close;

  // Focus the first enabled item.
  const firstEnabled = items.findIndex((li) => !li.classList.contains('disabled'));
  if (firstEnabled >= 0) {
    setHighlight(firstEnabled);
  }
}
